'use client'

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'

import { chakra } from '@chakra-ui/react'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import { useWorkspaceMembers } from '#features/workspace-members/use-workspace-members'

import { type MentionItem, createSuggestion } from './prompt-suggestion'

/**
 * Serialize a TipTap document to plain text with mention tokens.
 * Mention nodes become `@[Name](member:id)`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function serializeToText(doc: any): string {
  let text = ''

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  doc.content?.forEach((block: any, blockIndex: number) => {
    if (blockIndex > 0) {
      text += '\n'
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    block.content?.forEach((node: any) => {
      if (node.type === 'mention') {
        const label = node.attrs?.label || node.attrs?.id || ''
        const id = node.attrs?.id || ''
        text += `@[${label}](member:${id})`
      } else if (node.type === 'text') {
        text += node.text || ''
      } else if (node.type === 'hardBreak') {
        text += '\n'
      }
    })
  })

  return text
}

export interface PromptEditorRef {
  focus: () => void
  clear: () => void
  getText: () => string
}

export interface PromptEditorProps {
  value?: string
  onChange?: (text: string) => void
  onSubmit?: () => void
  /**
   * Which key combo triggers onSubmit:
   * - 'enter': plain Enter submits, Shift+Enter for newline (default)
   * - 'mod-enter': Cmd/Ctrl+Enter submits, Enter for newline
   * Only applies when onSubmit is provided.
   */
  submitKey?: 'enter' | 'mod-enter'
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
  minHeight?: string
  workspaceId: string
}

export const PromptEditor = forwardRef<PromptEditorRef, PromptEditorProps>(
  function PromptEditor(props, ref) {
    const {
      onChange,
      onSubmit,
      submitKey = 'enter',
      placeholder,
      disabled = false,
      autoFocus = false,
      minHeight = '6em',
      workspaceId,
    } = props

    const membersData = useWorkspaceMembers(workspaceId)

    const membersRef = useRef<MentionItem[]>([])

    membersRef.current = useMemo(() => {
      return membersData
        .filter((m) => m.status === 'active')
        .map((m) => ({
          id: m.id,
          name: m.name ?? m.email ?? 'Unknown',
          image: m.image ?? null,
        }))
    }, [membersData])

    const suggestion = useMemo(
      () =>
        createSuggestion(({ query }) => {
          return membersRef.current
            .filter((item) =>
              item.name.toLowerCase().startsWith(query.toLowerCase()),
            )
            .slice(0, 5)
        }),
      [],
    )

    const onSubmitRef = useRef(onSubmit)
    onSubmitRef.current = onSubmit

    const submitKeyRef = useRef(submitKey)
    submitKeyRef.current = submitKey

    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    const editor = useEditor({
      immediatelyRender: false,
      editable: !disabled,
      onUpdate({ editor }) {
        const text = serializeToText(editor.getJSON())
        onChangeRef.current?.(text)
      },
      extensions: [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          codeBlock: false,
          horizontalRule: false,
          bulletList: false,
          orderedList: false,
        }),
        Placeholder.configure({ placeholder }),
        Mention.configure({
          HTMLAttributes: { class: 'mention' },
          suggestion,
          renderLabel({ options, node }) {
            return `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`
          },
        }),
      ],
      editorProps: {
        handleKeyDown(view, event) {
          if (!onSubmitRef.current || event.key !== 'Enter') return false

          const key = submitKeyRef.current

          // mod-enter: submit on Cmd/Ctrl+Enter
          if (key === 'mod-enter') {
            if (event.metaKey || event.ctrlKey) {
              event.preventDefault()
              onSubmitRef.current()
              return true
            }
            return false
          }

          // enter: submit on plain Enter (no modifiers), Shift+Enter for newline
          if (
            event.shiftKey ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey
          ) {
            return false
          }

          // Don't submit if the mention suggestion popup is open
          const { state } = view
          for (const plugin of state.plugins) {
            const pluginState = plugin.getState(state)
            if (pluginState?.active) {
              return false
            }
          }

          event.preventDefault()
          onSubmitRef.current()
          return true
        },
      },
    })

    // Sync disabled state
    useEffect(() => {
      if (editor && editor.isEditable === disabled) {
        editor.setEditable(!disabled)
      }
    }, [editor, disabled])

    // Auto-focus
    useEffect(() => {
      if (autoFocus && editor) {
        // Delay to ensure the editor is ready
        const timer = setTimeout(() => editor.commands.focus(), 50)
        return () => clearTimeout(timer)
      }
    }, [autoFocus, editor])

    const getText = useCallback(() => {
      if (!editor) return ''
      return serializeToText(editor.getJSON())
    }, [editor])

    useImperativeHandle(ref, () => ({
      focus() {
        editor?.commands.focus()
      },
      clear() {
        editor?.commands.clearContent()
      },
      getText,
    }))

    const editorStyles = {
      width: '100%',
      '& .ProseMirror': {
        minH: minHeight,
        outline: 0,
        width: '100%',
        fontSize: 'sm',
      },
      '& .ProseMirror p.is-editor-empty:first-of-type::before': {
        color: 'fg.muted',
        content: 'attr(data-placeholder)',
        float: 'left',
        height: 0,
        pointerEvents: 'none',
      },
      wordBreak: 'break-word',
      '& .mention': {
        backgroundColor: 'colorPalette.subtle',
        color: 'colorPalette.fg',
        borderRadius: 'sm',
        px: '4px',
        py: '1px',
      },
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'text',
    }

    return (
      <chakra.div
        css={editorStyles}
        colorPalette="accent"
        onClick={() => editor?.commands.focus()}
      >
        <EditorContent editor={editor} />
      </chakra.div>
    )
  },
)
